import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Mail, RefreshCw, Loader2, Send, Clock, ChevronDown, ChevronUp,
  Inbox, AlertCircle, RotateCw, Trash2,
} from 'lucide-react';
import { formatApiErrorDetail } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'queued', label: 'Queued' },
  { id: 'sent', label: 'Sent' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

export const EmailOutboxPanel = () => {
  const [tab, setTab] = useState('all');
  const [data, setData] = useState({ items: [], total: 0, queued: 0, sent: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async (status) => {
    setError('');
    try {
      const res = await axios.get(`${API}/admin/email-outbox`, { params: { status, limit: 50 } });
      setData(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { setLoading(true); load(tab); }, [tab, load]);

  const handleRefresh = () => { setRefreshing(true); load(tab); };

  const retry = async (id) => {
    setActingId(id);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/email-outbox/${id}/retry`);
      setToast(res.data?.sent ? 'Email delivered.' : `Retry failed: ${res.data?.last_error || 'unknown'}`);
      setTimeout(() => setToast(''), 2600);
      await load(tab);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setActingId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this outbox entry? This is permanent.')) return;
    setActingId(id);
    setError('');
    try {
      await axios.delete(`${API}/admin/email-outbox/${id}`);
      setToast('Outbox entry deleted.');
      setTimeout(() => setToast(''), 2000);
      await load(tab);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setActingId(null);
    }
  };

  return (
    <section className="glass-card rounded-2xl border border-slate-800 p-6 space-y-5" data-testid="admin-email-outbox">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#00f0ff]" />
          <h3 className="font-display font-bold text-sm text-white uppercase tracking-wide">Email Outbox</h3>
          <span className="font-mono text-[9px] text-slate-500 uppercase tracking-widest border border-slate-800 bg-slate-900/60 px-2 py-0.5 rounded ml-1">
            {data.total} total · {data.queued} queued · {data.sent} sent
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-900/60 border border-slate-800 p-1 rounded">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-2.5 py-1 rounded font-mono text-[10px] uppercase tracking-widest font-bold transition-all cursor-pointer ${tab === t.id ? 'bg-[#00f0ff]/15 text-[#00f0ff]' : 'text-slate-400 hover:text-slate-200'}`}
                data-testid={`outbox-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-2.5 py-1 rounded border border-[#00f0ff]/30 bg-[#00f0ff]/5 text-[#00f0ff] hover:bg-[#00f0ff]/15 disabled:opacity-50 font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-all cursor-pointer"
            data-testid="btn-outbox-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {toast && (
        <div className="rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-3 py-2 text-[11px] font-mono" data-testid="outbox-toast">
          {toast}
        </div>
      )}
      {error && (
        <div className="rounded border border-rose-500/30 bg-rose-500/10 p-3 text-rose-300 text-xs font-mono flex items-start gap-2" data-testid="outbox-error">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="border border-slate-900 rounded-lg overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 flex items-center justify-center text-slate-500 font-mono text-xs">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading outbox…
          </div>
        ) : data.items.length === 0 ? (
          <div className="px-6 py-10 flex flex-col items-center justify-center gap-2 text-slate-500 font-mono text-xs" data-testid="outbox-empty">
            <Inbox className="w-5 h-5" />
            <span>No {tab === 'all' ? '' : tab} emails in the outbox.</span>
          </div>
        ) : (
          <ul className="divide-y divide-slate-900" data-testid="outbox-list">
            {data.items.map((m) => {
              const isOpen = openId === m.id;
              return (
                <li key={m.id} className="hover:bg-slate-900/40 transition-colors" data-testid={`outbox-row-${m.id}`}>
                  <button
                    onClick={() => setOpenId(isOpen ? null : m.id)}
                    className="w-full px-4 py-3 flex items-start gap-3 text-left cursor-pointer"
                  >
                    <span
                      className={`mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-widest border ${m.sent ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-amber-500/40 bg-amber-500/10 text-amber-300'}`}
                      data-testid={`outbox-status-${m.id}`}
                    >
                      {m.sent ? <><Send className="w-2.5 h-2.5" /> Sent</> : <><Clock className="w-2.5 h-2.5" /> Queued</>}
                    </span>
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                        <span className="font-mono text-[11px] text-[#00f0ff] truncate">{m.to}</span>
                        <span className="font-mono text-[9px] text-slate-500 uppercase tracking-widest">From {m.from}</span>
                      </div>
                      <div className="text-xs text-slate-200 font-medium truncate">{m.subject}</div>
                    </div>
                    <span className="font-mono text-[9px] text-slate-500 whitespace-nowrap">{formatDate(m.queued_at)}</span>
                    {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
                  </button>
                  {isOpen && (
                    <div className="bg-slate-950/60 border-t border-slate-900 px-5 py-3 space-y-2" data-testid={`outbox-detail-${m.id}`}>
                      {(m.provider_id || m.sent_at || m.provider) && (
                        <div className="flex flex-wrap gap-3 font-mono text-[9px] text-slate-500 uppercase tracking-widest">
                          {m.provider && <span>Provider: <span className="text-slate-300">{m.provider}</span></span>}
                          {m.provider_id && <span>ID: <span className="text-[#00f0ff]">{m.provider_id}</span></span>}
                          {m.sent_at && <span>Sent at: <span className="text-emerald-300">{formatDate(m.sent_at)}</span></span>}
                        </div>
                      )}
                      {m.last_error && (
                        <div className="border border-rose-500/30 bg-rose-500/10 text-rose-300 text-[11px] font-mono px-3 py-2 rounded flex items-start gap-2" data-testid={`outbox-error-${m.id}`}>
                          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                          <span className="break-words">{m.last_error}</span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {!m.sent && (
                          <button
                            onClick={(e) => { e.stopPropagation(); retry(m.id); }}
                            disabled={actingId === m.id}
                            className="px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500 hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer"
                            data-testid={`btn-outbox-retry-${m.id}`}
                          >
                            {actingId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                            <span>Retry send</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); remove(m.id); }}
                          disabled={actingId === m.id}
                          className="px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 bg-rose-500/15 border border-rose-500/40 text-rose-300 hover:bg-rose-500 hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer"
                          data-testid={`btn-outbox-delete-${m.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                          <span>Delete</span>
                        </button>
                      </div>
                      <pre
                        className="text-slate-300 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words m-0"
                        data-testid={`outbox-body-${m.id}`}
                      >
{m.body}
                      </pre>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="font-mono text-[10px] text-slate-500 border-t border-slate-900/60 pt-3 leading-relaxed">
        Showing latest 50. All approval / rejection notifications are dispatched via{' '}
        <span className="text-[#00f0ff]">Resend</span> from{' '}
        <span className="text-[#00f0ff]">no-reply@rishabsalgotra.com</span>. While the sender domain
        is unverified, the Resend sandbox only delivers to the API-key owner — verify your domain at{' '}
        <span className="text-slate-300">resend.com/domains</span> to send to all recipients.
      </div>
    </section>
  );
};
